import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
} from '@mui/material';
import { toast } from 'react-toastify';
import DataContext from '../../../Contexts/DataContext';

/**
 * CreateDepartmentDialog — controlled MUI Dialog for creating a new department
 * inline within the Pro Forma wizard.
 *
 * Props:
 *   open         {boolean}               — controls dialog visibility
 *   entityId     {string}                — pre-filled entity ID (read-only context)
 *   entityName   {string}                — entity name displayed for context (read-only)
 *   onClose      {() => void}            — called on cancel or backdrop click; no context call
 *   onCreated    {(department) => void}  — called with the created department object on success
 */
const CreateDepartmentDialog = ({ open, entityId, entityName, onClose, onCreated }) => {
  const context = useContext(DataContext);

  const [form, setForm] = useState({ department_id: '', department_name: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset form state whenever the dialog opens
  useEffect(() => {
    if (open) {
      setForm({ department_id: '', department_name: '' });
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const newErrors = {};
    const trimmedId = String(form.department_id).trim();
    const trimmedName = String(form.department_name).trim();

    if (!trimmedId) {
      newErrors.department_id = 'Department ID is required';
    } else if (!/^\d+$/.test(trimmedId)) {
      newErrors.department_id = 'Department ID must be numeric';
    } else {
      // Client-side duplicate check: department_id must be unique within this entity
      const existingDepts = context.activeProjectAttributes?.departments || [];
      const isDuplicate = existingDepts.some(
        (d) =>
          String(d.entityId).trim() === String(entityId).trim() &&
          String(d.departmentId).trim() === trimmedId
      );
      if (isDuplicate) {
        newErrors.department_id = `Department ID ${trimmedId} already exists in this entity`;
      }
    }

    if (!trimmedName) {
      newErrors.department_name = 'Department Name is required';
    }

    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        entity_id: entityId,
        department_id: String(form.department_id).trim(),
        department_name: String(form.department_name).trim(),
      };
      const result = await context.createDepartment(payload);
      if (result) {
        onCreated(result);
        handleClose();
      } else {
        // Backend rejected — most likely a duplicate that slipped past the client check
        setErrors({ department_id: `Department ID ${payload.department_id} already exists in this entity. Choose a different ID.` });
      }
    } catch (err) {
      console.error('Error creating department:', err);
      toast.error('Failed to create department. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({ department_id: '', department_name: '' });
    setErrors({});
    onClose();
  };

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear the error for this field as the user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Department</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Entity"
            size="small"
            fullWidth
            value={entityId && entityName ? `${entityId} – ${entityName}` : entityId || ''}
            disabled
            inputProps={{ readOnly: true }}
          />
          <TextField
            label="Department ID *"
            size="small"
            fullWidth
            value={form.department_id}
            onChange={handleFieldChange('department_id')}
            error={!!errors.department_id}
            helperText={errors.department_id || 'Must be numeric (e.g. 200)'}
            disabled={saving}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="Department Name *"
            size="small"
            fullWidth
            value={form.department_name}
            onChange={handleFieldChange('department_name')}
            error={!!errors.department_name}
            helperText={errors.department_name}
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Creating...' : 'Create Department'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDepartmentDialog;
